import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';
import { isDesktop } from 'react-device-detect';
import { FaTimes } from '../../../util/reactIcons';
import { setShowExplorer } from '../../../actions/LayoutActions';
import { StudiesExplorer } from './StudiesExplorer';
import { ExplorerWrapper, ExplorerHeader, CloseButton, StyledTabsWrapper, StyledTab, ExplorerNavbar, VersionNumber } from './styled/StyledExplorer';
import { toNaturalName } from '../../../util/toNaturalName';
import ShowExplorerTab from './ShowExplorerTab';
import { LoginData } from '../../../util/LoginData';

const StyledName = styled.span`
  color: ${(props) => props.theme.colors.greyWhite};
`;

const STUDY_TAB = 'study';
const HISTORY_TAB = 'history';
const versionPackage = require('../../../../package.json');

const Explorer = ({ fetchComplete, showExplorer }) => {
  const { authenticated, username } = LoginData();

  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();

  const config = useSelector((state) => state.config);
  const patient = useSelector((state) => state.patient);

  const WITH_PATIENT_DISCLOSED = config.WITH_PATIENT_DISCLOSED;

  const patientHasHistory = patient.patientHistory && patient.patientHistory.length > 1 && config.WITH_PATIENT_HISTORY;
  const [currentTab, setCurrentTab] = useState(STUDY_TAB);
  const [isExplorerMounted, setIsExplorerMounted] = useState(showExplorer);
  const [isExplorerVisible, setIsExplorerVisible] = useState(showExplorer);
  const [isCompactViewport, setIsCompactViewport] = useState(() => (
    typeof window !== 'undefined'
      && window.matchMedia(`(max-width: ${theme.sizes.tablet})`).matches
  ));
  const previousShowExplorer = useRef(showExplorer);
  const unmountTimeout = useRef();

  useEffect(() => {
    const mediaQuery = window.matchMedia(`(max-width: ${theme.sizes.tablet})`);
    const updateViewportSize = () => setIsCompactViewport(mediaQuery.matches);
    updateViewportSize();
    mediaQuery.addEventListener('change', updateViewportSize);
    return () => mediaQuery.removeEventListener('change', updateViewportSize);
  }, [theme.sizes.tablet]);

  useEffect(() => {
    clearTimeout(unmountTimeout.current);

    if (showExplorer) {
      setIsExplorerMounted(true);
      if (!previousShowExplorer.current) {
        setIsExplorerVisible(false);
        const frame = window.requestAnimationFrame(() => setIsExplorerVisible(true));
        previousShowExplorer.current = true;
        return () => window.cancelAnimationFrame(frame);
      }
      setIsExplorerVisible(true);
    } else {
      previousShowExplorer.current = false;
      setIsExplorerVisible(false);
      if (!isCompactViewport) {
        unmountTimeout.current = setTimeout(() => setIsExplorerMounted(false), 350);
      }
    }

    return () => clearTimeout(unmountTimeout.current);
  }, [showExplorer, isCompactViewport]);

  const handleExplorerTransitionEnd = (event) => {
    if (
      event.target === event.currentTarget
      && event.propertyName === 'opacity'
      && !showExplorer
      && !isCompactViewport
    ) {
      clearTimeout(unmountTimeout.current);
      setIsExplorerMounted(false);
    }
  };

  const getPatientSex = (elem) => {
    switch (elem.PatientSex) {
      case 'F':
        return <span>{t('explorer.female')}</span>;
      case 'M':
        return <span>{t('explorer.male')}</span>;
      default:
        return '';
    }
  };

  const getPatientAge = (elem) => {
    switch (typeof elem.PatientAge) {
      case 'number':
        return elem.PatientAge;
      case 'string':
        return parseInt(elem.PatientAge.replace(/\D/g, ''), 10);
      default:
        return '';
    }
  };

  return (
    <>
      {(isCompactViewport || isExplorerMounted) && (
      <ExplorerWrapper
        $isOpen={showExplorer}
        $isVisible={isExplorerVisible}
        aria-hidden={!showExplorer}
        onTransitionEnd={handleExplorerTransitionEnd}
      >
        <ExplorerNavbar>
          <VersionNumber>
            {`version: ${versionPackage.version.replace('-', '.')}`}
            {authenticated && `, user: ${username}`}
          </VersionNumber>
          {isDesktop
            && (
            <CloseButton onClick={() => dispatch(setShowExplorer(false))}>
              <FaTimes size="1.3em" />
            </CloseButton>
          )}
        </ExplorerNavbar>
        <ExplorerHeader>
          {WITH_PATIENT_DISCLOSED && <StyledName>{toNaturalName(patient.PatientName)}</StyledName>}
          {WITH_PATIENT_DISCLOSED && <span>{patient.PatientID}</span>}
          {WITH_PATIENT_DISCLOSED && patient.PatientAge !== undefined && (
            <span>
              {`${getPatientAge(patient)} ${t('explorer.yearsold')}`}
            </span>
          )}
          {WITH_PATIENT_DISCLOSED && patient.PatientWeight && <span>{`${patient.PatientWeight} Kg`}</span>}
          {WITH_PATIENT_DISCLOSED && patient.PatientSex !== undefined && getPatientSex(patient)}
          {/* {patient.ReferringPhysicianName && <span>{JSON.stringify(patient.ReferringPhysicianName)} Referer</span>} */}
          {config.WITH_PATIENT_HISTORY && patient.patientHistory && `${patient.patientHistory.length} ${t('explorer.studies')}`}
        </ExplorerHeader>
        <StyledTabsWrapper>
          <StyledTab onClick={() => setCurrentTab(STUDY_TAB)} className={STUDY_TAB === currentTab ? 'selected' : ''}>
            <span>{t('explorer.study')}</span>
          </StyledTab>
          {patientHasHistory && (
            <StyledTab onClick={() => setCurrentTab(HISTORY_TAB)} className={HISTORY_TAB === currentTab ? 'selected' : ''}>
              <span>{t('explorer.history')}</span>
            </StyledTab>
          )}
        </StyledTabsWrapper>
        { fetchComplete
          && <StudiesExplorer currentTab={currentTab} setCurrentTab={setCurrentTab} tabOptions={{ STUDY_TAB, HISTORY_TAB }} />}
      </ExplorerWrapper>
      )}
      {!isDesktop
        && <ShowExplorerTab />}
    </>
  );
};

export default Explorer;
